import type {
  FiatStepResponse,
  JsonValue,
  Quote,
} from '@fun-xyz/fiat-contract/types'
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import { sendAuthedFiatTransition } from '~/domains/fiat/authedTransport'
import {
  type GetIdentityAssertion,
  type IdentityAssertionProvider,
  createIdentityAssertionProvider,
} from '~/domains/fiat/identityAssertion'
import type { ComputePageOptions } from '~/domains/fiat/computePage'
import { computePage } from '~/domains/fiat/computePage'
import {
  describeStep,
  endpointShape,
  isDeadEnd,
} from '~/domains/fiat/describeStep'
import { transitionTarget } from '~/domains/fiat/transitionTarget'
import { redactFiatBodyForLog } from '~/domains/fiat/fiatLogRedactor'
import type {
  SubmitTransition,
  SurfaceTransition,
  TransitionDispatch,
} from '~/domains/fiat/selectTransitions'
import { buildPollEndpoint } from '~/domains/fiat/selectTransitions'
import { schedulePoll } from '~/domains/fiat/schedulePoll'
import type { CollectedInputs, SurfaceResult } from '~/domains/fiat/submitBody'
import type { FunLogger } from '~/utils/funLogger'
import { useEffectEvent } from '../useEffectEvent'
import type { FiatFlowState, FiatPhase } from './fiatFlowReducer'
import {
  fiatFlowReducer,
  initialFiatFlowState,
  isPolling,
} from './fiatFlowReducer'
import { useSubmitter } from './useSubmitter'

/**
 * How `/fiat/*` requests authenticate. One object rather than two optional
 * props, because a minter without a subject is the identity-crossing bug: the
 * cache has to be scoped to *who* the assertion speaks for, and the callback's own
 * identity cannot say. A host that inlines the callback passes a new function
 * every render, so rebuilding on that would defeat the cache, while never
 * rebuilding would let a user switch keep spending the previous user's assertion
 * until it expired. Pairing them makes the subject impossible to forget.
 */
export interface FiatIdentityAssertion {
  /** Mints the host-signed identity assertion sent as the bearer credential. */
  getAssertion: GetIdentityAssertion
  /** Who the assertion speaks for — the signed-in user's id. */
  subject: string
}

export interface UseFiatTransitionsParams {
  apiKey: string
  logger: FunLogger
  /** Omitted only while a flow has no signed-in user to speak for. */
  identityAssertion?: FiatIdentityAssertion
  /** Address that receives the purchased crypto. */
  depositAddress?: string
  /**
   * Whether a confirmed delivery ends the order — true only where the
   * provider pays the recipient itself. See {@link ComputePageOptions}.
   */
  settlesOnDelivery?: boolean
}

export interface FiatFlow {
  phase: FiatPhase
  page: FiatFlowState['page']
  stepResponse?: FiatStepResponse
  quote?: Quote
  identityAssertionProvider?: IdentityAssertionProvider
  ctas: TransitionDispatch['ctas']
  surface: TransitionDispatch['surface']
  collected: CollectedInputs
  isPolling: boolean
  isInFlight: boolean
  error: unknown
  submitError: unknown
  receive: (stepResponse: FiatStepResponse) => void
  setField: (name: string, value: JsonValue) => void
  /** Submits a collected transition, optionally adding values produced atomically by a provider surface. */
  submit: (
    cta: SubmitTransition,
    additionalInputs?: CollectedInputs,
  ) => Promise<void>
  reportSurface: (
    result: SurfaceResult,
    transition?: SurfaceTransition,
  ) => Promise<void>
  retry: () => Promise<void>
  exit: () => void
}

/** Drives transitions, submissions, and polling for one fiat flow. */
export function useFiatTransitions({
  apiKey,
  logger,
  identityAssertion,
  depositAddress,
  settlesOnDelivery,
}: UseFiatTransitionsParams): FiatFlow {
  const [state, dispatch] = useReducer(fiatFlowReducer, initialFiatFlowState)

  const receive = useCallback(
    (stepResponse: FiatStepResponse) => {
      dispatch({
        type: 'STEP_RESPONSE_RECEIVED',
        stepResponse,
        pageOptions: { settlesOnDelivery },
      })
    },
    [settlesOnDelivery],
  )

  // One cache across renders even when the host inlines the callback, so the
  // provider cannot be memoized on the callback itself. `useEffectEvent`
  // rather than a ref assigned during render: a render React discards still
  // moves a ref, which would let a mint call a callback that was never
  // committed. The subject is what actually bounds the cache, so switching
  // users starts a new one instead of reusing an assertion minted for the last.
  const mintAssertion = useEffectEvent(() =>
    identityAssertion === undefined
      ? Promise.reject(new Error('identity assertion was removed mid-flow'))
      : identityAssertion.getAssertion(),
  )
  const canMintAssertion = identityAssertion !== undefined
  const assertionSubject = identityAssertion?.subject
  const identityAssertionProvider = useMemo(
    () =>
      canMintAssertion
        ? createIdentityAssertionProvider(mintAssertion)
        : undefined,
    [canMintAssertion, assertionSubject, mintAssertion],
  )

  const submitter = useSubmitter({
    apiKey,
    logger,
    identityAssertionProvider,
    depositAddress,
    onStepResponse: receive,
  })

  const submitting = useRef(false)
  /** What the last fire was, so a retry can say what it is replaying. */
  const lastFired = useRef<FiredTransition>({ id: '-', endpoint: '-' })

  /**
   * Runs one transition and says what it did.
   *
   * The failure path already logs. What was missing is the other half: which
   * transition fired and where it went.
   *
   * - DEBUG for the ordinary pair: every step emits them, so they are for
   *   following one session, not for watching the rail.
   * - WARN for a step that offers nothing: the screen may render, but there
   *   is no way on from it.
   */
  const runSubmission = useCallback(
    async (
      fired: FiredTransition,
      send: () => Promise<FiatStepResponse | undefined>,
    ) => {
      if (submitting.current) {
        return
      }
      submitting.current = true
      lastFired.current = fired
      dispatch({ type: 'SUBMIT_STARTED' })
      try {
        logger.debug('connect-core:fiat:transition_fired', fired)
        const stepResponse = await send()
        if (stepResponse === undefined) {
          dispatch({ type: 'SUBMIT_FAILED' })
          return
        }
        const settled = { ...fired, ...describeStep(stepResponse) }
        if (isDeadEnd(stepResponse)) {
          logger.warn('connect-core:fiat:transition_dead_end', settled)
        } else {
          logger.debug('connect-core:fiat:transition_settled', settled)
        }
      } finally {
        submitting.current = false
      }
    },
    [logger],
  )

  const submit = useCallback(
    (cta: SubmitTransition, additionalInputs?: CollectedInputs) =>
      runSubmission(
        { id: cta.id, endpoint: endpointShape(transitionTarget(cta).endpoint) },
        () =>
          submitter.fire({
            transition: cta,
            collected: { ...state.collected, ...additionalInputs },
          }),
      ),
    [runSubmission, submitter, state.collected],
  )

  const reportSurface = useCallback(
    async (result: SurfaceResult, transition?: SurfaceTransition) => {
      // A surface carried by a quote is drawn straight from the quote and
      // never reaches the reducer, so its caller passes the transition to
      // report with. Only a surface the reducer owns can be looked up here.
      const surface = transition ?? state.dispatch.surface
      if (surface === undefined) {
        return
      }
      dispatch({ type: 'SURFACE_SETTLED' })
      await runSubmission(
        {
          id: surface.id,
          endpoint: endpointShape(transitionTarget(surface).endpoint),
        },
        () => submitter.fire({ transition: surface, surfaceResult: result }),
      )
    },
    [runSubmission, submitter, state.dispatch],
  )

  // The submitter replays the attempt it kept, so the transition being sent
  // is the last one fired rather than anything the caller names here.
  const retry = useCallback(
    () => runSubmission({ ...lastFired.current, retry: true }, submitter.retry),
    [runSubmission, submitter],
  )

  const setField = useCallback((name: string, value: JsonValue) => {
    dispatch({ type: 'FIELD_CHANGED', name, value })
  }, [])

  const exit = useCallback(() => dispatch({ type: 'EXITED' }), [])

  const onPollGaveUp = useCallback((error: unknown) => {
    dispatch({ type: 'POLL_GAVE_UP', error })
  }, [])

  usePollRegion({
    apiKey,
    logger,
    identityAssertionProvider,
    state,
    receive,
    onPollGaveUp,
  })

  return {
    phase: state.phase,
    page: state.page,
    stepResponse: state.stepResponse,
    quote: state.quote,
    identityAssertionProvider,
    ctas: state.dispatch.ctas,
    surface: state.dispatch.surface,
    collected: state.collected,
    isPolling: isPolling(state),
    isInFlight: submitter.isInFlight,
    error: state.error,
    submitError: submitter.error,
    receive,
    setField,
    submit,
    reportSurface,
    retry,
    exit,
  }
}

/** Which transition a log line is about. */
interface FiredTransition {
  id: string
  endpoint: string
  retry?: boolean
}

/** Polls an AWAIT transition until the flow moves to another page. */
function usePollRegion({
  apiKey,
  logger,
  identityAssertionProvider,
  state,
  receive,
  onPollGaveUp,
}: {
  apiKey: string
  logger: FunLogger
  identityAssertionProvider: IdentityAssertionProvider | undefined
  state: FiatFlowState
  receive: (stepResponse: FiatStepResponse) => void
  onPollGaveUp: (error: unknown) => void
}): void {
  const polling = isPolling(state)
  const poll = state.dispatch.poll
  const startedOnIdentity = state.page.identity

  const onStepResponse = useEffectEvent((stepResponse: FiatStepResponse) =>
    receive(stepResponse),
  )
  const onGiveUp = useEffectEvent(() => {
    logger.warn('connect-core:fiat:poll_gave_up', {
      identity: startedOnIdentity,
    })
    // The deadline is the one failure a poll region produces itself, so it is
    // also the one error the reducer cannot read off a step response.
    onPollGaveUp(
      new Error(`fiat poll gave up before the flow left ${startedOnIdentity}`),
    )
  })

  useEffect(() => {
    if (!polling || poll === undefined) {
      return
    }

    const controller = new AbortController()
    let disposed = false

    const cancel = schedulePoll(poll.poll, {
      onPoll: async () => {
        const stepResponse = await sendAuthedFiatTransition<FiatStepResponse>(
          {
            apiKey,
            logger,
            signal: controller.signal,
            redactBodyForLog: redactFiatBodyForLog,
            params: { endpoint: buildPollEndpoint(poll.poll) },
          },
          identityAssertionProvider,
        )
        if (disposed) {
          return false
        }
        onStepResponse(stepResponse)
        return computePage(stepResponse).identity === startedOnIdentity
      },
      onGiveUp,
      onError: (error) => {
        if (isAbort(error)) {
          return
        }
        logger.warn('connect-core:fiat:poll_failed', {
          identity: startedOnIdentity,
          reason: error instanceof Error ? error.name : 'unknown',
        })
      },
    })

    return () => {
      disposed = true
      controller.abort()
      cancel()
    }
  }, [
    polling,
    poll,
    startedOnIdentity,
    apiKey,
    logger,
    identityAssertionProvider,
    onStepResponse,
    onGiveUp,
  ])
}

const isAbort = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError'
